/** Known table header rows from AlbaVox legal docx exports (column order matters). */
const KNOWN_TABLE_HEADERS: readonly (readonly string[])[] = [
  ["Category", "Source / examples", "Section"],
  [
    "Purpose",
    "Categories of data used",
    "Legal basis (GDPR Art. 6)",
    "Retention",
  ],
  ["Category", "What it does", "Consent status"],
  ["Name / tool", "Category", "Provider", "Purpose", "Retention"],
  ["Recipient category", "Examples / role", "Legal status"],
  ["Term", "Meaning"],
  ["Right", "What it means"],
  ["Action", "Description"],
] as const;

export type ParsedTable = {
  headers: string[];
  rows: string[][];
};

export type LegalContentBlock =
  | { type: "prose"; text: string }
  | { type: "table"; table: ParsedTable };

function normalizeLine(line: string): string {
  return line.trim().replace(/\s+/g, " ");
}

function linesMatchHeaders(
  lines: string[],
  start: number,
  headers: readonly string[],
): boolean {
  if (start + headers.length > lines.length) return false;
  return headers.every(
    (header, j) => normalizeLine(lines[start + j] ?? "") === normalizeLine(header),
  );
}

function findTableHeader(
  lines: string[],
  start: number,
): { headers: string[]; columnCount: number } | null {
  for (const headers of KNOWN_TABLE_HEADERS) {
    if (linesMatchHeaders(lines, start, headers)) {
      return { headers: [...headers], columnCount: headers.length };
    }
  }
  return null;
}

/** True when a row chunk is clearly prose after the table, not tabular data. */
function chunkLooksLikeProse(chunk: string[]): boolean {
  const first = chunk[0]?.trim() ?? "";
  if (!first) return false;
  if (/^\d+\.\d+\s/.test(first)) return false;
  if (/^(Account|Content|Location|Payment|Access|Rectification|Erasure|Hosting|Analytics|session_id|Strictly)/i.test(first))
    return false;
  return (
    first.length > 70 &&
    /[.!?]$/.test(first) &&
    !first.startsWith("Performance of") &&
    !first.startsWith("You provide")
  );
}

function extractTableAt(
  lines: string[],
  headerIndex: number,
): { table: ParsedTable; endIndex: number } | null {
  const match = findTableHeader(lines, headerIndex);
  if (!match) return null;

  const dataStart = headerIndex + match.columnCount;
  const rows: string[][] = [];
  let pos = dataStart;

  while (pos + match.columnCount <= lines.length) {
    const chunk = lines
      .slice(pos, pos + match.columnCount)
      .map((l) => l.trim());

    if (chunk.every((c) => !c)) {
      pos += match.columnCount;
      continue;
    }

    if (chunkLooksLikeProse(chunk)) break;

    rows.push(chunk);
    pos += match.columnCount;
  }

  if (rows.length === 0) return null;

  return {
    table: { headers: match.headers, rows },
    endIndex: pos,
  };
}

/**
 * Split legal section body into prose paragraphs and structured tables.
 */
export function parseLegalContent(text: string): LegalContentBlock[] {
  const lines = text.split(/\r?\n/);
  const blocks: LegalContentBlock[] = [];
  let proseBuffer: string[] = [];
  let i = 0;

  const flushProse = () => {
    const joined = proseBuffer.join("\n").trim();
    if (joined) {
      blocks.push({ type: "prose", text: joined });
    }
    proseBuffer = [];
  };

  while (i < lines.length) {
    const extracted = extractTableAt(lines, i);
    if (extracted) {
      flushProse();
      blocks.push({ type: "table", table: extracted.table });
      i = extracted.endIndex;
      continue;
    }

    proseBuffer.push(lines[i] ?? "");
    i += 1;
  }

  flushProse();
  return blocks;
}
